/**
 * O vocabulário de ícones do Noesis.
 *
 * **Por que um arquivo de vocabulário, e não o glifo direto na tela.** O nome
 * que interessa ao app é o papel — "revisar", "erro", "ofensiva" —, não o
 * desenho. Assim a tela lê `IconeRevisar` e não `"revisar"`, trocar o desenho
 * é uma linha aqui em vez de uma busca por todo o `telas/`, e a pergunta "que
 * ícone significa erro no Noesis?" tem um lugar com resposta.
 *
 * Os desenhos moram em `glifos.ts`, que é o sprite do handoff de UI/UX (ADR
 * 039). O Phosphor saiu do app junto com ele.
 */
import type { Glifo } from "./glifos.js";

// --- navegação ------------------------------------------------------------
export const IconeInicio: Glifo = "inicio";
export const IconeEstudar: Glifo = "estudar";
export const IconeRevisar: Glifo = "revisar";
export const IconeSimulado: Glifo = "simulado";
export const IconeNumeros: Glifo = "numeros";
export const IconeCaderno: Glifo = "caderno";
export const IconeFontes: Glifo = "fontes";
export const IconeImportar: Glifo = "importar";
export const IconeTrilhas: Glifo = "trilha";
export const IconeAjustes: Glifo = "ajustes";
/* O "Mais" da faixa inferior. Ele usava o glifo de Ajustes, que é **um dos
   destinos guardados atrás dele** — o mesmo desenho abrindo o painel e sendo
   um item do painel. */
export const IconeMais: Glifo = "mais";
export const IconeConta: Glifo = "conta";
/* A IA é ferramenta do estudo, não personagem: brilho, não robô. */
export const IconeIa: Glifo = "ia";

// --- ritual ---------------------------------------------------------------
// Os dois únicos que andam em duotone. A ofensiva é a assinatura do app.
export const IconeOfensiva: Glifo = "chama";
export const IconeMeta: Glifo = "meta";

// --- acervo e prazo -------------------------------------------------------
export const IconeAcervo: Glifo = "acervo";
export const IconeDataAlvo: Glifo = "bandeira";

// --- estados de estudo ----------------------------------------------------
// Cada um é o **segundo sinal** da cor semântica que o acompanha (seção 4 da
// identidade visual): cor sozinha exclui quem não a distingue.
export const IconeAcerto: Glifo = "check_circle";
export const IconeErro: Glifo = "x_circle";
export const IconeDevido: Glifo = "relogio";
export const IconeDuvida: Glifo = "duvida";
export const IconeMarcado: Glifo = "marcador";
export const IconeAnotar: Glifo = "nota";

// --- ações ----------------------------------------------------------------
export const IconeSeguir: Glifo = "arrow_right";
export const IconeVoltar: Glifo = "chevron_left";
export const IconeAvancar: Glifo = "chevron_right";
/* O calendario do seletor de dia do heatmap (RN-56). `IconeDataAlvo` e uma
   bandeira e significa outra coisa: a data em que a prova acontece. */
export const IconeCalendario: Glifo = "calendario";
export const IconeCriar: Glifo = "plus";
export const IconeExcluir: Glifo = "lixeira";
export const IconeSalvar: Glifo = "salvar";
export const IconeExportar: Glifo = "download";
export const IconeSair: Glifo = "sair";
export const IconeBuscar: Glifo = "buscar";
export const IconeFechar: Glifo = "x";
export const IconeBarraLateral: Glifo = "barra_lateral";

// --- avisos ---------------------------------------------------------------
export const IconeSemSinal: Glifo = "sem_sinal";
export const IconeAviso: Glifo = "aviso";
export const IconeOnibus: Glifo = "onibus";

// --- barra de formatação do caderno (RN-40) ---
export const IconeForte: Glifo = "forte";
export const IconeItalico: Glifo = "italico";
export const IconeSublinhado: Glifo = "sublinhado";
export const IconeRiscado: Glifo = "riscado";
export const IconeBorracha: Glifo = "borracha";
export const IconeCodigo: Glifo = "codigo";
export const IconeTitulo: Glifo = "titulo";
export const IconeListaSimples: Glifo = "lista";
export const IconeListaNumerada: Glifo = "lista_num";
export const IconeCitacao: Glifo = "citacao";
export const IconeAlinharEsquerda: Glifo = "alinhar_esquerda";
export const IconeAlinharCentro: Glifo = "alinhar_centro";
export const IconeAlinharDireita: Glifo = "alinhar_direita";

/* Os dois gatilhos de menu da barra, e a seta que diz que eles abrem algo.
   O "Aa" carrega cor, realce e tamanho; o justificado carrega o alinhamento —
   cada um é o desenho do que o menu contém, não um ícone genérico de "mais". */
export const IconeAparencia: Glifo = "aparencia";
export const IconeAlinhamento: Glifo = "justificar";
export const IconeAbrirMenu: Glifo = "chevron_down";

/** Diagnóstico de erros (RN-46): a lupa sobre o que deu errado. */
export const IconeDiagnostico: Glifo = "diagnostico";

/** Encerrar a sessão, ou entregar a prova (RN-52, RN-54). */
export const IconeEncerrar: Glifo = "encerrar";

// --- barra de ferramentas do caderno (Fase 2) -----------------------------
/* Os dez grupos da barra da Fase 2 pediram desenhos que o vocabulário ainda
   não tinha. Mesma regra de sempre: um módulo por ícone, nomeado pelo papel. */
export const IconeDesfazer: Glifo = "desfazer";
export const IconeRefazer: Glifo = "refazer";
export const IconeJustificar: Glifo = "justificar";
export const IconeListaDeTarefas: Glifo = "checklist";
export const IconeAumentarRecuo: Glifo = "aumentar_recuo";
export const IconeDiminuirRecuo: Glifo = "diminuir_recuo";
export const IconeBlocoDeCodigo: Glifo = "bloco_codigo";
export const IconeLinhaHorizontal: Glifo = "linha";
export const IconeTabela: Glifo = "tabela";
export const IconeLink: Glifo = "link";
export const IconeRemoverLink: Glifo = "remover_link";
export const IconeLimparFormatacao: Glifo = "borracha";
export const IconeMarcaTexto: Glifo = "marca_texto";
export const IconeCorDoTexto: Glifo = "cor_texto";
export const IconePagina: Glifo = "pagina";
export const IconeChevron: Glifo = "chevron_right";
export const IconeOrdenar: Glifo = "ordenar";
export const IconeConferido: Glifo = "check";
export const IconeEnviar: Glifo = "upload";
export const IconeMaisGrade: Glifo = "grade";
